import React, { memo, useCallback, useEffect, useState } from 'react'
import { Image } from 'react-native'

import { useFocusEffect } from '@react-navigation/native'
import { WorldOutlined } from '@zet/agent-website/assets/svgs'
import { publishEvent } from '@zet/analytics'
import { useCopilot } from '@zet/common/components/coachMarks'
import Tag from '@zet/common/components/tag'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { Icon, colors as zetColors } from '@zet/design-system'
import Avatar from '@zet/design-system/avatar/Avatar'
import { Chip } from '@zet/design-system/chip'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B4, B5, SH3 } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { useBrandDetailsById } from '@zet/feature-brands/api/brands/hooks'
import { LeadStatusCard } from '@zet/feature-leads/components/leadStatusCard'
import { i18n } from '@zet/localization'
import { HDFC_ASKCODES, LeadStatus, PaymentStatus } from '@zet/utils/Constants'
import { LeadCardProps, PaymentChipProps } from '@zet/utils/interface'
import storageUtil from '@zet/utils/storage'

import { CoachMarks } from './components/coachmark'
import { MagnetCrossSellingStrip } from './components/magnetCrossSellingStrip'
import PayoutText from './payoutText'

const LeadCard = memo(
  ({
    id,
    name,
    phoneNumber,
    lastUpdateDate,
    nextUpdateDate,
    isBonusLead,
    status,
    brandCommission,
    commissionType,
    subAgentName,
    hasNotMoved,
    paymentStatus,
    rejectionReason,
    pendingDays,
    isDelayedByBrand,
    commission,
    title,
    description,
    isReopenAllowed,
    isShareNewLink,
    isReopened,
    partnerId,
    eventName,
    eventPayload,
    isCustomerSpecific = false,
    askCode,
    extraCommission = 0,
    isMagnetEligible,
    showCrossSelling,
    crossSellingPartnerName,
    noOfLeads,
    isFetching,
    source,
  }: LeadCardProps) => {
    const { data: content } = useBrandDetailsById(partnerId)
    const value = useCopilot()
    const [isLayoutRendered, setIsLayoutRendered] = useState(false)

    useFocusEffect(
      useCallback(() => {
        const isLeadCoachMarkShown = storageUtil.getObject('leadCoachMarkShown')
        if (
          noOfLeads &&
          noOfLeads === 1 &&
          !isFetching &&
          !isLeadCoachMarkShown &&
          APP_NAME !== MY_TEAMS_ADVISOR
        ) {
          value?.start()
        }
        return () => {
          value?.stop()
        }
      }, [isLayoutRendered, isFetching]),
    )

    useEffect(() => {
      value?.copilotEvents.on('stop', () => {
        storageUtil.saveObject('leadCoachMarkShown', true)
        publishEvent('click_action', {
          item_name: 'lead_status_date_coachmark',
        })
      })
    }, [value?.copilotEvents])

    const statusColor = () => {
      if (askCode === HDFC_ASKCODES.$ARN_REQUIRED) {
        return 'warning'
      } else if (askCode === HDFC_ASKCODES.$ARN_INCORRECT) {
        return 'error'
      } else if (hasNotMoved) {
        return 'warning'
      } else {
        return 'active'
      }
    }

    const setIntent = ():
      | 'warning'
      | 'active'
      | 'error'
      | 'success'
      | undefined => {
      switch (status) {
        case LeadStatus.LEAD_ADDED:
          return hasNotMoved ? 'warning' : 'active'
        case LeadStatus.ACTION_REQUIRED:
          return statusColor()
        case LeadStatus.FULLFILLED:
        case LeadStatus.PARTIALLY_FULLFILLED:
          return 'success'
        case LeadStatus.EXPIRED:
        case LeadStatus.REJECTED:
          return 'error'

        default:
          return undefined
      }
    }
    const { navigate } = useNavigation()

    const toProperCase = (str: string) => {
      return str
        ?.toLowerCase()
        ?.split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    const PaymentChip = ({
      primaryIconType,
      primaryIconColor,
      message,
      primaryIconSize,
    }: PaymentChipProps) => {
      const renderHighlightedMessage = (text: string) => {
        if (!text || typeof text !== 'string') {
          return text
        }

        const sanitizedText = text.replace(/[<>]/g, '')

        const currencyRegex = /(₹|$|€|£|¥)(\d{1,10}(?:,\d{3})*(?:\.\d{1,2})?)/g

        const parts = sanitizedText.split(currencyRegex)
        const elements: React.ReactNode[] = []

        for (let i = 0; i < parts.length; i += 3) {
          const beforeText = parts[i]
          const currencySymbol = parts[i + 1]
          const amount = parts[i + 2]

          if (beforeText && beforeText.trim()) {
            elements.push(beforeText)
          }

          if (
            currencySymbol &&
            amount &&
            /^\d{1,10}(?:,\d{3})*(?:\.\d{1,2})?$/.test(amount)
          ) {
            elements.push(
              <B4 key={`currency-${i}`} className="text-tertiary-100">
                {currencySymbol}
                {amount}
              </B4>,
            )
          }
        }

        return elements.length > 0 ? elements : sanitizedText
      }

      return (
        <XStack className="bg-grays-20 rounded p-2 mt-2 items-center">
          <Icon
            type={primaryIconType}
            color={primaryIconColor}
            size={primaryIconSize}
          />
          <B4 className={`text-grays-70 ml-2`}>
            {renderHighlightedMessage(message)}
          </B4>
        </XStack>
      )
    }

    const paymentChipContent = () => {
      if (APP_NAME === MY_TEAMS_ADVISOR) {
        return <></>
      }
      switch (paymentStatus) {
        case PaymentStatus.PENDING:
          return (
            <PaymentChip
              primaryIconType="RupeeCircleOutlined"
              primaryIconColor={zetColors.primary[100]}
              message={i18n.t('myLeads.paymentPending', { commission })}
              primaryIconSize={24}
            />
          )
        case PaymentStatus.PAID:
          return (
            <PaymentChip
              primaryIconType="RupeeCircleFilled"
              primaryIconColor={zetColors.success[100]}
              message={i18n.t('myLeads.paymentDone', { commission })}
            />
          )

        case PaymentStatus.REJECTED:
          return (
            <PaymentChip
              primaryIconType="RupeeCircleOutlined"
              primaryIconColor={zetColors.error[100]}
              message={i18n.t('myLeads.paymentRejected', { commission })}
              primaryIconSize={24}
            />
          )
        case PaymentStatus.FAILED:
          return (
            <PaymentChip
              primaryIconType="RupeeCircleOutlined"
              primaryIconColor={zetColors.warning[100]}
              message={i18n.t('myLeads.paymentFailed', { commission })}
              primaryIconSize={24}
            />
          )
        case PaymentStatus.EXPIRED:
          return (
            <PaymentChip
              message={i18n.t('myLeads.paymentExpired', { commission })}
              primaryIconType="ExpiredFilled"
              primaryIconColor={zetColors.error[150]}
              primaryIconSize={20}
            />
          )

        default:
          return (
            <PaymentChip
              primaryIconType="RupeeCircleOutlined"
              primaryIconColor={zetColors.primary[100]}
              message={i18n.t('myLeads.paymentPending', { commission })}
              primaryIconSize={24}
            />
          )
      }
    }

    const setLeadStatusCardContent = () => {
      switch (status) {
        case LeadStatus.LEAD_ADDED:
          return {
            title: isReopened
              ? i18n.t('myLeads.reshared')
              : i18n.t('myLeads.shared'),
            description: isReopened
              ? i18n.t('leads.linkSharedDescription')
              : i18n.t('leads.linkSharedDescription'),
          }

        case LeadStatus.ACTION_REQUIRED:
          return {
            title: title,
            description: description,
          }

        case LeadStatus.REJECTED:
          return {
            title: i18n.t('myLeads.reject'),
            description:
              rejectionReason ||
              i18n.t('leads.rejectionDescription', {
                brandName: content?.name,
              }),
          }

        case LeadStatus.EXPIRED:
          return {
            title: i18n.t('myLeads.expire'),
            description: rejectionReason,
          }

        case LeadStatus.PARTIALLY_FULLFILLED:
          return {
            title: i18n.t('myLeads.partly'),
          }

        case LeadStatus.FULLFILLED:
          return {
            title: i18n.t('myLeads.completed'),
            description: i18n.t('myLeads.completedDes', { lastUpdateDate }),
          }
      }
    }

    const calculateBrandCommission = () => {
      const res = brandCommission + extraCommission
      switch (commissionType) {
        case 'percentage':
          return Math.round(res * 100) / 100
        default:
          return brandCommission + Math.trunc(extraCommission)
      }
    }

    const getCrossSellingEventAttribute = () => {
      if (showCrossSelling && APP_NAME !== MY_TEAMS_ADVISOR) {
        return {
          cross_selling_strip: isMagnetEligible
            ? 'magent_cross_selling_strip'
            : 'other_brands_cross_selling_strip',
        }
      }
      return {}
    }

    return (
      <Pressable
        onPress={() => navigate('LeadProgress', { leadId: id })}
        eventName={eventName}
        eventPayload={{
          ...eventPayload,
          brand_name: content?.name,
          brand_category: content?.subCategory,
          ...getCrossSellingEventAttribute(),
        }}
      >
        <Stack
          onLayout={() => setIsLayoutRendered(true)}
          className={`justify-center ${
            isBonusLead ? 'pb-4/2 pt-[36]' : 'py-4'
          } bg-white  px-3  `}
        >
          {APP_NAME !== MY_TEAMS_ADVISOR && source === 'agent-website' && (
            <XStack className="bg-primary-10 items-center self-start rounded p-2">
              <WorldOutlined
                color={zetColors.primary[100]}
                height={16}
                width={16}
              />
              <B5 className="text-primary-150 ml-2">
                {i18n.t('addedFromWeb')}
              </B5>
            </XStack>
          )}
          {isBonusLead && (
            <Tag
              colors={[zetColors.tag.shade1, zetColors.tag.shade2]}
              className="absolute left-3 top-0 rounded-b "
              icon={require('../../assets/images/gift.png')}
              title={i18n.t('myLeads.bonus')}
            />
          )}
          {hasNotMoved && (
            <Chip
              primaryIconType="ErrorOutlined"
              primaryIconColor={zetColors.warning[100]}
              className=" rounded "
            >
              <B5 className=" text-grays-60 ">{i18n.t('myLeads.notMoved')} </B5>
            </Chip>
          )}
          {(status === LeadStatus.LEAD_ADDED ||
            status === LeadStatus.ACTION_REQUIRED) &&
            isDelayedByBrand && (
              <Chip
                primaryIconType="CautionOutlined"
                primaryIconColor={zetColors.error[100]}
                className=" rounded "
              >
                <B5 className=" text-grays-60 ">
                  {i18n.t('myLeads.delayed')}{' '}
                </B5>
              </Chip>
            )}

          <XStack className=" mt-2  items-start justify-between ">
            {!isCustomerSpecific && (
              <XStack className=" flex-1  items-start justify-center">
                <Avatar size="large" name={name ?? 'Not Available'} />
                <Stack className="ml-4/2 flex-1  ">
                  <SH3 className=" flex-1  ">
                    {name ? toProperCase(name) : 'Not Available'}
                  </SH3>
                  <B5 className=" text-grays-50 ">
                    {phoneNumber
                      ? phoneNumber?.slice(0, 3) === '+91'
                        ? phoneNumber.replace('+91', '+91-')
                        : `+91-${phoneNumber}`
                      : 'Not Available'}
                  </B5>
                </Stack>
              </XStack>
            )}
            {Boolean(content?.logo) && (
              <Image
                className=" ml-1 h-6 w-[106] "
                source={{ uri: content?.logo }}
                resizeMode="contain"
              />
            )}
          </XStack>
          <SH3 className="text-primary-150 mt-2" numberOfLines={1}>
            {content?.name}
          </SH3>

          {APP_NAME !== MY_TEAMS_ADVISOR && subAgentName && (
            <Chip primaryIconType="LeadsOutlined" className=" mt-2 rounded ">
              <B5 className=" text-grays-60 ">
                {i18n.t('myLeads.addedBy', {
                  subAgentName,
                })}
              </B5>
            </Chip>
          )}

          {(status === LeadStatus.LEAD_ADDED ||
            status === LeadStatus.ACTION_REQUIRED) && (
            <Stack className="mt-3">
              <CoachMarks
                text={i18n.t('myLeads.leadStatus')}
                order={1}
                name="lead_status_date"
              >
                <XStack className=" bg-grays-20 px-4/2 items-center justify-center rounded-[4] py-2  ">
                  <Stack className="border-grays-40 flex-grow items-center justify-center border-r ">
                    <Stack className=" ml-2 self-start ">
                      <B5 className="text-grays-60">
                        {' '}
                        {i18n.t('myLeads.addedOn')}
                      </B5>
                      <B4 className="text-grays-80"> {lastUpdateDate} </B4>
                    </Stack>
                  </Stack>
                  <Stack className="flex-grow items-center justify-center ">
                    <Stack className=" ml-4 self-start ">
                      <B5 className="text-grays-60">
                        {''} {i18n.t('myLeads.nextUpdate')}
                      </B5>
                      <B4 className="text-grays-80"> {nextUpdateDate} </B4>
                    </Stack>
                  </Stack>
                </XStack>
              </CoachMarks>
            </Stack>
          )}
          <Stack className="mt-2">
            <CoachMarks
              text={i18n.t('myLeads.leadStatus')}
              order={2}
              name="lead_status"
            >
              <LeadStatusCard
                content={setLeadStatusCardContent()}
                intent={setIntent()}
              />
            </CoachMarks>
          </Stack>

          {isReopenAllowed && isShareNewLink && (
            <Chip primaryIconType="AnnounceOutlined" className=" mt-2 rounded ">
              <B5 className=" text-grays-60 ">{i18n.t('myLeads.reapply')} </B5>
            </Chip>
          )}

          {(status === LeadStatus.PARTIALLY_FULLFILLED ||
            status === LeadStatus.FULLFILLED ||
            status === LeadStatus.REJECTED ||
            status === LeadStatus.EXPIRED) &&
          commission ? (
            paymentChipContent()
          ) : (
            <></>
          )}

          {APP_NAME !== MY_TEAMS_ADVISOR && brandCommission
            ? (status === LeadStatus.LEAD_ADDED ||
                status === LeadStatus.ACTION_REQUIRED) && (
                <PayoutText
                  pendingDays={pendingDays}
                  commissionType={commissionType}
                  brandCommission={calculateBrandCommission()}
                />
              )
            : null}
        </Stack>
        {APP_NAME !== MY_TEAMS_ADVISOR && showCrossSelling && (
          <MagnetCrossSellingStrip
            isMagnetEligible={isMagnetEligible}
            partnerName={crossSellingPartnerName}
            customerName={name}
          />
        )}
      </Pressable>
    )
  },
)

export { LeadCard }
